// src/admin/components/sales/SaleDetailView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    RiArrowLeftLine, RiUserLine, RiBuildingLine, RiMoneyDollarCircleLine,
    RiCalendarLine, RiCheckLine, RiTimeLine, RiHandCoinLine,
    RiPhoneLine, RiRefreshLine, RiDeleteBin6Line, RiEditLine,
    RiAlarmWarningLine, RiCloseLine, RiPrinterLine
} from 'react-icons/ri';
import { getSale, recordInstallmentPayment, updateInstallment, deleteSale, formatCurrency } from '../../../models/saleModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import PrintReceipt from '../payments/PrintReceipt';
import toast from 'react-hot-toast';

const SaleDetailView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const isSales = user?.role === 'sales';
    const isManagement = isAdmin || user?.role === 'front_desk';
    const canSeeNextPayment = isAdmin || isSales || user?.role === 'front_desk';
    const isRTL = ['ar', 'ku'].includes(i18n.language);

    const [sale, setSale] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPrintModal, setShowPrintModal] = useState(false);

    // Pay flow
    const [payingId, setPayingId] = useState(null);
    const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
    const [confirmId, setConfirmId] = useState(null);

    // Edit installment flow (admin only)
    const [editId, setEditId] = useState(null);
    const [editAmount, setEditAmount] = useState('');
    const [editDueDate, setEditDueDate] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const fetchSale = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getSale(id);
            setSale(res.sale);
        } catch (err) {
            setError(err.message || t('failed_to_load_sale'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { fetchSale(); }, [fetchSale]);

    const handleMarkPaid = async (installmentId) => {
        try {
            setPayingId(installmentId);
            await recordInstallmentPayment(id, { installmentId, paymentDate: payDate });
            toast.success(t('payment_recorded'));
            setConfirmId(null);
            fetchSale();
        } catch (err) {
            toast.error(err.message || t('failed_to_record_payment'));
        } finally {
            setPayingId(null);
        }
    };

    const openEdit = (inst) => {
        setEditId(inst.id);
        setEditAmount(inst.amount);
        setEditDueDate(inst.dueDate ? inst.dueDate.split('T')[0] : '');
        setConfirmId(null);
    };

    const cancelEdit = () => { setEditId(null); };

    const handleSaveEdit = async () => {
        try {
            setSavingEdit(true);
            await updateInstallment(id, editId, { amount: editAmount, dueDate: editDueDate });
            toast.success('Installment updated');
            setEditId(null);
            fetchSale();
        } catch (err) {
            toast.error(err.message || 'Failed to update installment');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('delete_sale_all_confirm'))) return;
        try {
            await deleteSale(id);
            toast.success(t('sale_deleted'));
            navigate('/admin/sales');
        } catch (err) {
            toast.error(err.message || t('failed_to_delete_sale'));
        }
    };

    const cardClass = `rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`;

    if (loading) return (
        <div className="flex items-center justify-center py-24">
            <div className="animate-spin h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full" />
        </div>
    );

    if (error || !sale) return (
        <div className="p-6 text-center">
            <p className="text-red-400 mb-4">{error || t('sale_not_found')}</p>
            <Link to="/admin/sales" className="text-sm text-red-500 hover:underline">← {t('back_to_owners')}</Link>
        </div>
    );

    const pct = sale.totalPrice > 0 ? Math.min(100, Math.round((sale.paidAmount / sale.totalPrice) * 100)) : 0;
    const STATUS = {
        paid:    { key: 'sale_paid',    cls: 'bg-green-100 text-green-800',  clsDark: 'bg-green-900/20 text-green-400' },
        partial: { key: 'sale_partial', cls: 'bg-yellow-100 text-yellow-800', clsDark: 'bg-yellow-900/20 text-yellow-400' },
        unpaid:  { key: 'sale_unpaid', cls: 'bg-red-100 text-red-800',    clsDark: 'bg-red-900/20 text-red-400' },
    };
    const status = STATUS[sale.paymentStatus] || STATUS.unpaid;

    // Compute next pending installment
    const pendingInstallments = (sale.installments || []).filter(i => i.status !== 'paid');
    const nextInstallment = pendingInstallments.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0] || null;
    const nextDueDate = nextInstallment ? new Date(nextInstallment.dueDate) : null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isNextOverdue = nextDueDate && nextDueDate < today;
    const daysUntilNext = nextDueDate ? Math.ceil((nextDueDate - today) / (1000 * 60 * 60 * 24)) : null;

    const fmtDate = (d) => new Date(d).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin/sales')}
                        className={`p-2 rounded-lg flex-shrink-0 ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600'}`}
                    >
                        <RiArrowLeftLine size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                {t('sale_detail')} #{sale.saleNumber ?? sale.id}
                            </h1>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${isDarkMode ? status.clsDark : status.cls}`}>
                                {t(status.key)}
                            </span>
                        </div>
                        <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            {new Date(sale.saleDate || sale.createdAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowPrintModal(true)}
                        className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900'}`}
                        title={t('print')}
                    >
                        <RiPrinterLine size={18} />
                    </button>
                    <button onClick={fetchSale} className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-500'}`}>
                        <RiRefreshLine size={18} />
                    </button>
                    {isAdmin && (
                        <button onClick={handleDelete} className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-red-900/40 text-zinc-400 hover:text-red-400' : 'bg-zinc-100 hover:bg-red-50 text-zinc-500 hover:text-red-600'}`}
                            title={t('delete')}>
                            <RiDeleteBin6Line size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Next Payment Banner — visible to admin, front_desk, and sales */}
            {canSeeNextPayment && nextInstallment && sale.paymentStatus !== 'paid' && (
                <div className={`mb-5 rounded-xl border px-5 py-4 flex items-center justify-between gap-4 flex-wrap
                    ${isNextOverdue
                        ? isDarkMode ? 'border-red-700 bg-red-900/20' : 'border-red-300 bg-red-50'
                        : daysUntilNext <= 7
                            ? isDarkMode ? 'border-yellow-700 bg-yellow-900/20' : 'border-yellow-300 bg-yellow-50'
                            : isDarkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
                    <div className="flex items-center gap-3">
                        <RiAlarmWarningLine
                            size={22}
                            className={isNextOverdue ? 'text-red-500' : daysUntilNext <= 7 ? 'text-yellow-500' : 'text-blue-500'}
                        />
                        <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-0.5
                                ${isNextOverdue ? 'text-red-500' : daysUntilNext <= 7 ? 'text-yellow-600' : 'text-blue-600'}`}>
                                {isNextOverdue ? 'Payment Overdue' : 'Next Payment Due'}
                            </p>
                            <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                {fmtDate(nextInstallment.dueDate)}
                                {isNextOverdue
                                    ? <span className="ml-2 text-red-400 text-xs font-medium">({Math.abs(daysUntilNext)} days ago)</span>
                                    : daysUntilNext === 0
                                        ? <span className="ml-2 text-yellow-400 text-xs font-medium">(Today)</span>
                                        : <span className={`ml-2 text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                            (in {daysUntilNext} day{daysUntilNext !== 1 ? 's' : ''})
                                        </span>}
                            </p>
                        </div>
                    </div>
                    <div className="text-end">
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>Amount due</p>
                        <p className={`text-lg font-bold ${isNextOverdue ? 'text-red-400' : isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                            {formatCurrency(nextInstallment.amount)}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            {pendingInstallments.length} installment{pendingInstallments.length !== 1 ? 's' : ''} remaining
                        </p>
                    </div>
                </div>
            )}

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {/* Buyer */}
                <div className={cardClass}>
                    <div className="flex items-center gap-2 mb-3">
                        <RiUserLine className="text-red-500" size={18} />
                        <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('buyer')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">{sale.buyerName?.charAt(0)}</span>
                        </div>
                        <div>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{sale.buyerName}</p>
                            <p className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                <RiPhoneLine size={13} />{sale.buyerPhone}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Apartment */}
                <div className={cardClass}>
                    <div className="flex items-center gap-2 mb-3">
                        <RiBuildingLine className="text-red-500" size={18} />
                        <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('apartments')}</span>
                    </div>
                    {sale.apartmentNumber ? (
                        <div>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{sale.apartmentNumber}</p>
                            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{sale.buildingName}</p>
                            {sale.floor && <p className="text-xs text-zinc-500">{t('floor')} {sale.floor} • {sale.size} m² • {sale.apartmentType}</p>}
                        </div>
                    ) : (
                        <p className="text-zinc-400 text-sm">—</p>
                    )}
                </div>

                {/* Financials */}
                <div className={cardClass}>
                    <div className="flex items-center gap-2 mb-3">
                        <RiMoneyDollarCircleLine className="text-red-500" size={18} />
                        <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('financials')}</span>
                    </div>
                    <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                            <span className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}>{t('total_sale_price')}</span>
                            <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{formatCurrency(sale.totalPrice)}</span>
                        </div>
                        {sale.commissionAmount > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}>
                                    {t('commission')} {sale.commissionType === 'percent' && sale.commissionRate != null ? `(${sale.commissionRate}%)` : ''}
                                </span>
                                <span className="text-red-500 font-medium">− {formatCurrency(sale.commissionAmount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm">
                            <span className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}>{t('collected')}</span>
                            <span className="text-green-500 font-medium">{formatCurrency(sale.paidAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}>{t('balance')}</span>
                            <span className={`font-medium ${sale.balance > 0 ? 'text-red-400' : 'text-green-400'}`}>{formatCurrency(sale.balance)}</span>
                        </div>
                        {sale.commissionAmount > 0 && (
                            <div className={`flex justify-between text-sm pt-1.5 mt-1 border-t ${isDarkMode ? 'border-zinc-700' : 'border-zinc-200'}`}>
                                <span className={`font-semibold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>{t('net_to_company')}</span>
                                <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{formatCurrency(sale.netToCompany)}</span>
                            </div>
                        )}
                    </div>
                    {sale.commissionAmount > 0 && sale.commissionNotes && (
                        <p className={`text-xs mt-2 italic ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            {sale.commissionNotes}
                        </p>
                    )}
                </div>
            </div>

            {/* Progress */}
            <div className={`${cardClass} mb-5`}>
                <div className="flex items-center justify-between mb-3">
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{t('payment_progress')}</span>
                    <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{pct}%</span>
                </div>
                <div className={`h-3 rounded-full ${isDarkMode ? 'bg-zinc-700' : 'bg-zinc-200'}`}>
                    <div
                        className={`h-3 rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <div className="flex justify-between mt-2 text-xs text-zinc-500">
                    <span>{formatCurrency(sale.paidAmount)} {t('collected')}</span>
                    <span>{formatCurrency(sale.balance)} {t('outstanding')}</span>
                </div>
            </div>

            {/* Installments Table */}
            <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white'}`}>
                <div className={`px-5 py-4 border-b flex items-center justify-between ${isDarkMode ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-zinc-50'}`}>
                    <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        {t('installments')} ({sale.installments?.length || 0})
                    </h2>
                    {isManagement && confirmId && (
                        <div className="flex items-center gap-2">
                            <label className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('payment_date_label2')}</label>
                            <input
                                type="date"
                                value={payDate}
                                onChange={e => setPayDate(e.target.value)}
                                className={`text-xs px-2 py-1 rounded border ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}
                            />
                        </div>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-500 bg-zinc-950' : 'text-zinc-500 bg-zinc-50'}`}>
                                <th className="px-5 py-3 text-start">#</th>
                                <th className="px-5 py-3 text-start">{t('due_date')}</th>
                                <th className="px-5 py-3 text-start">{t('amount')}</th>
                                <th className="px-5 py-3 text-start">{t('status')}</th>
                                <th className="px-5 py-3 text-start">{t('payment_date')}</th>
                                {(isManagement || isAdmin || isSales) && <th className="px-5 py-3 text-center">{t('actions')}</th>}
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-zinc-100'}`}>
                            {(sale.installments || []).map((inst, idx) => {
                                const isPaid = inst.status === 'paid';
                                const isOverdue = !isPaid && new Date(inst.dueDate) < today;
                                const isEditing = editId === inst.id;
                                return (
                                    <tr key={inst.id} className={`transition-colors ${isDarkMode ? 'hover:bg-zinc-800/40' : 'hover:bg-zinc-50/50'}`}>
                                        <td className={`px-5 py-4 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{idx + 1}</td>

                                        {/* Due Date cell — editable for admin */}
                                        <td className={`px-5 py-4 text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    value={editDueDate}
                                                    onChange={e => setEditDueDate(e.target.value)}
                                                    className={`text-xs px-2 py-1 rounded border w-36 ${isDarkMode ? 'bg-zinc-800 border-zinc-600 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`}
                                                />
                                            ) : (
                                                <span className="flex items-center gap-1.5">
                                                    <RiCalendarLine size={14} className="text-zinc-400" />
                                                    {new Date(inst.dueDate).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    {isOverdue && <span className="text-red-400 text-xs font-medium ms-1">{t('overdue')}</span>}
                                                </span>
                                            )}
                                        </td>

                                        {/* Amount cell — editable for admin */}
                                        <td className={`px-5 py-4 text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    value={editAmount}
                                                    onChange={e => setEditAmount(e.target.value)}
                                                    className={`text-xs px-2 py-1 rounded border w-28 ${isDarkMode ? 'bg-zinc-800 border-zinc-600 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`}
                                                    min="0"
                                                />
                                            ) : (
                                                formatCurrency(inst.amount)
                                            )}
                                        </td>

                                        <td className="px-5 py-4">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold
                                                ${isPaid
                                                    ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                                                    : isOverdue
                                                        ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                                                        : isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-600'}`}
                                            >
                                                {isPaid ? <RiCheckLine size={12} /> : <RiTimeLine size={12} />}
                                                {isPaid ? t('sale_paid') : isOverdue ? t('overdue') : t('pending')}
                                            </span>
                                        </td>

                                        <td className={`px-5 py-4 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                            {inst.paymentDate
                                                ? new Date(inst.paymentDate).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' })
                                                : '—'}
                                        </td>

                                        {(isManagement || isAdmin || isSales) && (
                                            <td className="px-5 py-4 text-center">
                                                {isEditing ? (
                                                    /* Save / Cancel edit row */
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={handleSaveEdit}
                                                            disabled={savingEdit}
                                                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors disabled:opacity-60"
                                                        >
                                                            {savingEdit
                                                                ? <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                                : <RiCheckLine size={13} />}
                                                            Save
                                                        </button>
                                                        <button onClick={cancelEdit} className={`px-2 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-zinc-100 text-zinc-600'}`}>
                                                            <RiCloseLine size={13} />
                                                        </button>
                                                    </div>
                                                ) : isPaid ? (
                                                    <span className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-zinc-300'}`}>—</span>
                                                ) : isManagement && confirmId === inst.id ? (
                                                    /* Confirm pay row — management only */
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => handleMarkPaid(inst.id)}
                                                            disabled={payingId === inst.id}
                                                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors disabled:opacity-60"
                                                        >
                                                            {payingId === inst.id
                                                                ? <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                                : <RiCheckLine size={13} />}
                                                            {t('confirm_payment')}
                                                        </button>
                                                        <button onClick={() => setConfirmId(null)} className={`px-2 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-zinc-100 text-zinc-600'}`}>
                                                            {t('cancel')}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    /* Normal action buttons */
                                                    <div className="flex items-center justify-center gap-2">
                                                        {isManagement && (
                                                            <button
                                                                onClick={() => setConfirmId(inst.id)}
                                                                className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
                                                            >
                                                                <RiHandCoinLine size={13} /> {t('mark_paid')}
                                                            </button>
                                                        )}
                                                        {(isAdmin || isSales) && (
                                                            <button
                                                                onClick={() => openEdit(inst)}
                                                                className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600'}`}
                                                                title="Edit installment"
                                                            >
                                                                <RiEditLine size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {showPrintModal && (
                <PrintReceipt
                    payment={{ ...sale, paymentType: 'sale' }}
                    type="sale"
                    onClose={() => setShowPrintModal(false)}
                />
            )}
        </div>
    );
};

export default SaleDetailView;
